import { Box, useTheme } from '@homebotapp/hb-react-component-catalog'
import { generateUniqueID } from '../../../helpers/id'

interface HBRELogoProps {
  maxWidth?: number
}

/**
 * The logo defaults to `width: 100%`
 *
 * The `maxWidth` set a max size for the logo, with the height scaling proportionally.
 */
export const HBRELogo = ({ maxWidth }: HBRELogoProps) => {
  const { theme } = useTheme()
  const isLight = theme.isLightThemeType

  const fills = {
    house: isLight ? `var(--brand-neutral-cool-900)` : `var(--brand-primary-500)`,
    magnifyingGlass: `var(--brand-hb-white)`,
    letters: isLight ? `var(--brand-neutral-cool-900)` : `var(--brand-hb-white)`
  }

  const altText = `Homebot Real Estate Services`

  const uniqueId = generateUniqueID()
  const logoId = `hbre-logo-${uniqueId}`
  const logoTitle = `hbre-logo-title-${uniqueId}`

  return (
    <Box as='div' maxWidth={maxWidth} display={'block'}>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 249 55'
        preserveAspectRatio='xMidYMid meet'
        width='100%'
        id={logoId}
        role='img'
        aria-labelledby={logoTitle}
      >
        <title id={logoTitle}>{altText}</title>
        <path
          fill={fills.house}
          d='M27.126.3a1.32 1.32 0 011.676 0l26.642 21.904c.957.786.4 2.338-.838 2.338h-3.98v26.192a3.957 3.957 0 01-3.958 3.957H10.024a3.957 3.957 0 01-3.957-3.957V24.542H1.322c-1.238 0-1.794-1.552-.838-2.338L27.126.3z'
        ></path>
        <path
          fill={fills.magnifyingGlass}
          fillRule='evenodd'
          d='M34.049 41.748l4.162 4.162a2.036 2.036 0 002.879-2.88l-4.162-4.161a10.833 10.833 0 001.877-6.119c0-6.015-4.86-10.874-10.874-10.874A10.863 10.863 0 0017.056 32.75c0 6.016 4.86 10.875 10.875 10.875 2.255 0 4.372-.685 6.118-1.877zm-6.11-2.192a6.8 6.8 0 006.805-6.805 6.8 6.8 0 00-6.805-6.806 6.8 6.8 0 00-6.806 6.806 6.8 6.8 0 006.806 6.805z'
          clipRule='evenodd'
        ></path>
        <path
          fill={fills.letters}
          d='M210.621 54.518a1.32 1.32 0 01-1.319-1.32V10.32c0-.728.591-1.319 1.319-1.319h36.247c.728 0 1.319.59 1.319 1.32v5.034a1.32 1.32 0 01-1.319 1.319h-27.924a1.32 1.32 0 00-1.319 1.32v27.532c0 .729.591 1.32 1.319 1.32h28.184c.728 0 1.319.59 1.319 1.319v5.034a1.32 1.32 0 01-1.319 1.32h-36.507zm7.004-17.225V27.28s0 1.397 1.314 1.397h24.613c.728 0 1.319.59 1.319 1.32v4.85a1.32 1.32 0 01-1.319 1.32h-23.437-1.176c-1.019 0-1.314 1.127-1.314 1.127zM102.619 54.654c-.729 0-1.319-.59-1.319-1.319v-42.88c0-.728.59-1.318 1.319-1.318h5.815c.728 0 1.319.59 1.319 1.319v42.88a1.32 1.32 0 01-1.319 1.318h-5.815zm-38.3 0A1.32 1.32 0 0163 53.335v-42.88c0-.728.59-1.318 1.32-1.318h5.814c.729 0 1.32.59 1.32 1.319v42.88c0 .728-.591 1.318-1.32 1.318H64.32zm2.192-16.393l.065-7.933a75.772 75.772 0 0138.82 0v7.933a67.818 67.818 0 00-38.885 0zM164.106 54.654a1.32 1.32 0 01-1.32-1.319v-42.88c0-.728.591-1.318 1.32-1.318h25.211c3.034 0 5.722.607 8.063 1.82 2.341 1.214 4.183 2.905 5.527 5.072 1.387 2.125 2.081 4.552 2.081 7.283 0 2.818-.759 5.354-2.276 7.608-1.474 2.254-3.468 4.053-5.983 5.397-2.47 1.3-5.223 1.951-8.258 1.951l-17.426-.007v15.074a1.32 1.32 0 01-1.32 1.32h-5.619zm33.926.004a1.32 1.32 0 01-1.045-.525l-15.083-20.027 8.973-1.625 15.169 20.108c.658.872.032 2.12-1.061 2.113l-6.953-.044zm-26.987-25.063c0 .728.59 1.319 1.318 1.319l15.263.006c1.517 0 2.926-.325 4.227-.975 1.3-.694 2.341-1.582 3.121-2.666a6.477 6.477 0 001.17-3.772c0-1.3-.39-2.449-1.17-3.446-.78-.997-1.821-1.8-3.121-2.406-1.301-.607-2.731-.91-4.292-.91h-15.197c-.729 0-1.319.59-1.319 1.319v11.53zM115.21 54.654a1.32 1.32 0 01-1.32-1.319v-42.88c0-.728.591-1.318 1.32-1.318h23.91c3.902 0 7.153.455 9.754 1.365 2.644.867 4.638 2.146 5.982 3.837 1.344 1.69 2.016 3.75 2.016 6.177 0 2.428-.802 4.53-2.406 6.308-1.561 1.733-3.88 2.969-6.958 3.706l.391-1.236a20.757 20.757 0 015.527 2.081 11.988 11.988 0 014.226 3.837c1.084 1.604 1.626 3.576 1.626 5.917 0 2.688-.564 4.92-1.691 6.698a11.643 11.643 0 01-4.356 4.096c-1.821.997-3.815 1.713-5.983 2.146-2.167.39-4.313.585-6.437.585H115.21zm6.939-8.927c0 .729.59 1.32 1.319 1.32h17.603c1.907 0 3.576-.217 5.007-.65 1.474-.434 2.622-1.106 3.446-2.017.824-.953 1.236-2.124 1.236-3.511 0-1.604-.521-2.84-1.561-3.706-1.04-.867-2.341-1.474-3.902-1.821-1.56-.39-2.525-.817-4.551-.585-1.703.195-13.669 1.76-17.453 2.256a1.315 1.315 0 00-1.144 1.307v7.407zm0-17.445c0 .729.588 1.32 1.316 1.307 4.125-.076 11.38-.56 20.727-2.83 1.257-.434 2.254-1.063 2.991-1.886.737-.824 1.106-1.8 1.106-2.926 0-1.864-.867-3.187-2.601-3.967-1.691-.823-3.793-1.235-6.308-1.235h-15.912c-.729 0-1.319.59-1.319 1.319v10.218z'
        ></path>
      </svg>
    </Box>
  )
}
